const badge = (n: number) =>
  `<span style="background-color: #8551F5; color: #FFFFFF; font-weight: 700; font-size: 12px; width: 20px; height: 20px; min-width: 20px; border-radius: 500px; display: inline-block; text-align: center; line-height: 20px;">${n}</span>`;

// Uses a table instead of flexbox for the badge/title layout — flexbox `gap`
// has inconsistent support across email clients (notably Outlook desktop)
// and silently collapses to zero spacing instead of failing loudly. `td`
// padding is the universally-supported way to get reliable spacing in email.
const stepRow = ({
  n,
  title,
  isFirst = false,
}: {
  n: number;
  title: string;
  isFirst?: boolean;
}) =>
  `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-bottom: 24px;${isFirst ? " margin-top: 8px;" : ""}"><tbody><tr>` +
  `<td width="20" valign="middle" style="width: 20px;">${badge(n)}</td>` +
  `<td valign="middle" style="padding-left: 8px;"><span class="si-body-text" style="font-weight: 500;">${title}</span></td>` +
  `</tr></tbody></table>`;

const divider = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin: 32px 0;"><tbody><tr><td class="si-divider" style="height: 2px; line-height: 2px; font-size: 2px; background-color: #F9F8F4;">&nbsp;</td></tr></tbody></table>`;

export const fullWidthButton = ({
  href,
  label,
  clicktracking = false,
}: {
  href: string;
  label: string;
  clicktracking?: boolean;
}) =>
  `<a ${clicktracking ? 'clicktracking="off"' : 'rel="noopener noreferrer" referrerpolicy="no-referrer"'} target="_blank" href="${href}" style="text-decoration: none !important; color: #000000; display: block;">` +
  `<table width="100%" border="0" cellspacing="0" cellpadding="0" style="border-collapse: separate;"><tbody><tr>` +
  `<td height="48" style="color: #FFFFFF; background-color: #8551F5; border: none; cursor: pointer; text-align: center; vertical-align: middle; border-radius: 4px; padding: 0 16px; font-weight: 500; font-size: 15px; line-height: 26px;">${label}</td>` +
  `</tr></tbody></table></a>`;

export const orderEmailTemplate = ({
  tags,
  platform,
}: {
  tags: string[];
  platform: string;
}) =>
  [
    stepRow({ n: 1, title: "Order Product", isFirst: true }),
    fullWidthButton({
      href: "{{campaign.link}}?id={{onboard.onboardId}}&amp;stepType=order",
      label: "Order product",
    }),
    platform === "Amazon"
      ? `<p class="si-body-text" style="font-size: 12px; margin: 16px 0 0;">We use Amazon purchases for shipping products. <a href="https://stackinfluence.com/amazon-order-process/">Learn more</a></p>`
      : "",
    divider,
    stepRow({ n: 2, title: "Post on Instagram" }),
    `<ul class="si-body-text" style="margin: 0; padding-left: 20px;">` +
      `<li style="margin: 0 0 4px;">Post on Instagram with your face and the product visible. See <a clicktracking="off" href="https://app.stackinfluence.com/campaigns/{{campaign.contractId}}/{{campaign.campaignId}}" target="_blank">campaign and content guidelines</a></li>` +
      `<li style="margin: 0 0 4px;">Caption must mention product use and experience</li>` +
      `<li style="margin: 0 0 4px;">Required tags: <span style="font-weight: 600;">${tags.join(" ")}</span></li>` +
      `<li style="margin: 0;">Submit before deadline: <span style="font-weight: 600;">{{onboard.deadlineDate}}</span></li>` +
      `</ul>`,
    divider,
    stepRow({ n: 3, title: "Submit for Review" }),
    fullWidthButton({
      href: "https://app.stackinfluence.com/influencer/submit/{{onboard.onboardId}}/instagram",
      label: "Submit post to Instagram",
      clicktracking: true,
    }),
  ].join("");

// Not currently used in app, we'll need this if we enable TikTok self-serve campaigns.
export const orderEmailTemplateTiktok = ({
  tags,
  platform,
}: {
  tags: string[];
  platform: string;
}) =>
  [
    stepRow({ n: 1, title: "Order Product", isFirst: true }),
    fullWidthButton({
      href: "{{campaign.link}}?id={{onboard.onboardId}}&stepType=order",
      label: "Order product",
    }),
    platform === "Amazon"
      ? `<p class="si-body-text" style="font-size: 12px; margin: 16px 0 0;">We use Amazon purchases for shipping products. <a href="https://stackinfluence.com/amazon-order-process/">Learn more</a></p>`
      : "",
    divider,
    stepRow({ n: 2, title: "Post a TikTok Video" }),
    `<ul class="si-body-text" style="margin: 0; padding-left: 20px;">` +
      `<li style="margin: 0 0 4px;">Post a TikTok video with your face and the product visible. See <a clicktracking="off" href="https://app.stackinfluence.com/campaigns/{{campaign.contractId}}/{{campaign.campaignId}}" target="_blank">campaign and content guidelines</a></li>` +
      `<li style="margin: 0 0 4px;">Caption must mention product use and experience</li>` +
      `<li style="margin: 0 0 4px;">Required tags: <span style="font-weight: 600;">${tags.join(" ")}</span></li>` +
      `<li style="margin: 0;">Submit before deadline: <span style="font-weight: 600;">{{onboard.deadlineDate}}</span></li>` +
      `</ul>`,
    divider,
    stepRow({ n: 3, title: "Submit for Review" }),
    fullWidthButton({
      href: "https://app.stackinfluence.com/influencer/submit/{{onboard.onboardId}}/tiktok",
      label: "Submit post to TikTok",
      clicktracking: true,
    }),
  ].join("");

// Only used for brand website review steps, or any platform without a review email template.
export const reviewEmailTemplate = ({ companyName }: { companyName: string }) =>
  `<p class="si-body-text" style="margin: 0;">We sincerely appreciate feedback, please share your product experience on the <a rel="noopener noreferrer" referrerpolicy="no-referrer" target="_blank" href="{{campaign.link}}?id={{onboard.onboardId}}&amp;stepType=review"><span style="font-weight: 500;">${companyName} Website here</span></a></p>`;
